import { z } from 'zod';

import { orderStatusSchema } from '@/features/orders';
import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';

import { REPORT_RANGE_ERROR } from '../constants';

/** One row = one **Order** (CONTEXT.md "Row grain"). An order containing several devices is one row;
 *  the devices are aggregated into `itemCount` (AC6), which the backend already populates to
 *  112360's counting rule — a Bundle counts once however many Devices it holds, Components are
 *  counted, and quantity multiplies.
 *
 *  Field names are the backend's `OrderSummaryRow` verbatim (`libs/modules/src/reports/types/`).
 *  Raw wire values only, formatted client-side (ADR-0020): `phone` is bare digits, `orderAmount` a
 *  number, both dates ISO instants (`orderDate` null for a pre-placement order, feature 122355),
 *  `orderStatus` a wire token.
 *
 *  `deliveryDate` is nullable and **not** derived from the status in the UI: the backend returns it
 *  only for a delivered order (AC5), so the cell renders whatever arrived and never re-decides.
 *
 *  `purchasedFrom` is the backend's label ("Webstore" today) and `paymentMethod` its pre-formatted
 *  label — both rendered verbatim, never re-formatted (ADR-0020; grilling Q2, Q7). `soldOn` is the
 *  Eastern `YYYY-MM-DD` date of placement, null for a pre-placement order (feature 122355). */
export const orderSummaryRowSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  phone: z.string(),
  purchasedFrom: z.string(),
  orderAmount: z.number(),
  itemCount: z.number(),
  orderStatus: orderStatusSchema,
  orderDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  soldOn: z.string().nullable(),
  city: z.string(),
});

export const orderSummaryListResponseSchema = z.object({
  data: z.array(orderSummaryRowSchema),
  meta: pageMetaSchema,
});

/** RHF-draft validator for the filter panel. Every key is the backend's `OrderSummaryQuery` param
 *  verbatim (`libs/modules/src/reports/types/reports.types.ts`), so `orderSummaryToListQuery`
 *  renames nothing. Reports deliberately keeps this vocabulary rather than the internal
 *  `OrderListQuery`'s older `statuses`/`orderedFrom`/`deviceCountFrom` (backend design §2.2).
 *
 *  Only the two NUMERIC pairs are refined, and `enforceRangePairs` is number-only by design. A date
 *  refine was written and then removed as unreachable: rc-picker's `order` (on by default) SORTS the
 *  pair as it submits, so an inverted pick reaches `onChange` already swapped — verified against
 *  antd 6.4.3, where typing an earlier To into a reopened panel applies as
 *  `{orderDateFrom: <earlier>, orderDateTo: <later>}`. The AC's own defence for dates is the
 *  disabling in `DateRangeFieldset`, not a message. The message is ticket 01's `REPORT_RANGE_ERROR`,
 *  the AC-verbatim string shared by all seven Reports From/To pairs. */
export const orderSummaryFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    cities: z.array(z.string()),
    orderDateFrom: z.string().nullable(),
    orderDateTo: z.string().nullable(),
    deliveryDateFrom: z.string().nullable(),
    deliveryDateTo: z.string().nullable(),
    orderStatus: z.array(orderStatusSchema),
    amountFrom: z.number().nullable(),
    amountTo: z.number().nullable(),
    itemCountFrom: z.number().nullable(),
    itemCountTo: z.number().nullable(),
  })
  .superRefine(
    enforceRangePairs(
      [
        ['amountFrom', 'amountTo'],
        ['itemCountFrom', 'itemCountTo'],
      ],
      REPORT_RANGE_ERROR,
    ),
  );

/** One page of the City typeahead. `/orders/admin/meta/cities` returns **plain strings**, not
 *  `{id,name}` objects — the string is both the option value and the wire value. */
export const orderSummaryCityPageSchema = z.object({
  data: z.array(z.string()),
  meta: z.object({ nextCursor: z.string().optional(), hasNextPage: z.boolean() }),
});
